import {
  DollarSign,
  TrendingUp,
  CreditCard,
  Users as UsersIcon,
  Sparkles,
  RotateCcw,
  CalendarClock,
  Loader2,
  Crown,
} from 'lucide-react';

type Period = 'month' | '3months' | '6months' | 'year';

export interface StripeRevenueData {
  mrr: number;
  activeSubscriptions: number;
  monthlyPaidCount: number;
  monthlyPaidRevenue: number;
  lifetimePaidCount: number;
  lifetimePaidRevenue: number;
  creditPackCount: number;
  creditPackRevenue: number;
  otherRevenue: number;
  totalRefunds: number;
  totalPeriodIncome: number;
  revenueHistory: Array<{ month: string; revenue: number }>;
  paymentCount: number;
  payments: Array<{
    payment_intent_id: string;
    customer_id: string | null;
    customer_email: string | null;
    amount: number;
    type: string;
    date: string;
    description: string | null;
  }>;
}

interface RevenueBreakdownProps {
  stripeData: StripeRevenueData | null;
  stripeLoading: boolean;
  stripeError: string | null;
  newFreemiums: number;
  periodAiCost: number;
  revenuePeriod: Period;
  onPeriodChange: (period: Period) => void;
}

const fmt = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const RevenueBreakdown = ({
  stripeData,
  stripeLoading,
  stripeError,
  newFreemiums,
  periodAiCost,
  revenuePeriod,
  onPeriodChange,
}: RevenueBreakdownProps) => {
  const periodLabels: Record<Period, string> = {
    month: 'This Month',
    '3months': 'Last 3 Months',
    '6months': 'Last 6 Months',
    year: 'This Year',
  };

  const d = stripeData;
  const totalIncome = d?.totalPeriodIncome || 0;
  const refunds = d?.totalRefunds || 0;
  const totalCosts = periodAiCost + refunds;
  const netRevenue = totalIncome - totalCosts;

  return (
    <div className="p-5 sm:p-6 bg-white border border-border rounded-2xl shadow-sm">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center">
            <DollarSign className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground flex items-center gap-1.5">
              Revenue & Metrics
              <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                LIVE Stripe
              </span>
            </h3>
            <p className="text-[10px] text-muted-foreground">{periodLabels[revenuePeriod]}</p>
          </div>
        </div>

        <div className="flex gap-1">
          {(['month', '3months', '6months', 'year'] as const).map((p) => (
            <button
              key={p}
              onClick={() => onPeriodChange(p)}
              className={`px-2.5 py-1 text-[10px] sm:text-xs rounded-lg transition-colors ${
                revenuePeriod === p
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-muted-foreground hover:bg-secondary/80'
              }`}
            >
              {p === 'month' ? '1M' : p === '3months' ? '3M' : p === '6months' ? '6M' : '1Y'}
            </button>
          ))}
        </div>
      </div>

      {stripeLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-5 h-5 animate-spin text-primary mr-2" />
          <span className="text-sm text-muted-foreground">Loading Stripe data...</span>
        </div>
      ) : stripeError ? (
        <div className="p-4 bg-red-50 rounded-xl border border-red-200 text-sm text-red-700">
          <p className="font-medium">Failed to load Stripe data</p>
          <p className="text-xs mt-1">{stripeError}</p>
        </div>
      ) : (
        <>
          {/* Access Revenue */}
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Access Revenue
          </p>
          <div className="space-y-1.5 mb-4">
            <Row
              icon={CalendarClock}
              color="text-blue-600"
              bg="bg-blue-50"
              label="MRR (Expected Monthly)"
              sublabel={`${d?.activeSubscriptions || 0} active subs`}
              value={d?.mrr || 0}
            />
            <Row
              icon={CreditCard}
              color="text-blue-600"
              bg="bg-blue-50"
              label="Monthly Access Payments"
              sublabel={`${d?.monthlyPaidCount || 0} payments received`}
              value={d?.monthlyPaidRevenue || 0}
            />
            <Row
              icon={Crown}
              color="text-purple-600"
              bg="bg-purple-50"
              label="Lifetime Access Sales"
              sublabel={`${d?.lifetimePaidCount || 0} one-time sales`}
              value={d?.lifetimePaidRevenue || 0}
            />
          </div>

          {/* Credit Packs */}
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            Credit Pack Sales
          </p>
          <div className="space-y-1.5 mb-4">
            <Row
              icon={Sparkles}
              color="text-amber-600"
              bg="bg-amber-50"
              label="Credit Packs Sold"
              sublabel={`${d?.creditPackCount || 0} purchases`}
              value={d?.creditPackRevenue || 0}
            />
          </div>

          {/* New Members */}
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            New Members
          </p>
          <div className="space-y-1.5 mb-4">
            <CountRow
              icon={UsersIcon}
              color="text-slate-600"
              bg="bg-slate-50"
              label="New Freemium Signups"
              value={newFreemiums}
            />
            <CountRow
              icon={UsersIcon}
              color="text-blue-600"
              bg="bg-blue-50"
              label="New Monthly Subscribers"
              value={d?.monthlyPaidCount || 0}
            />
            <CountRow
              icon={UsersIcon}
              color="text-purple-600"
              bg="bg-purple-50"
              label="New Lifetime Members"
              value={d?.lifetimePaidCount || 0}
            />
          </div>

          {/* Totals */}
          <div className="border-t border-border pt-3 space-y-2">
            <div className="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
              <span className="text-xs font-semibold text-foreground">Total Income</span>
              <span className="text-lg font-bold text-foreground">${fmt(totalIncome)}</span>
            </div>
            <div className="flex items-center justify-between p-2.5 bg-red-50 rounded-lg">
              <span className="text-xs text-muted-foreground">AI API Costs</span>
              <span className="text-xs font-bold text-red-600">-${periodAiCost.toFixed(2)}</span>
            </div>
            {refunds > 0 && (
              <div className="flex items-center justify-between p-2.5 bg-red-50 rounded-lg">
                <span className="text-xs text-muted-foreground">Refunds</span>
                <span className="text-xs font-bold text-red-600">-${refunds.toFixed(2)}</span>
              </div>
            )}
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-semibold text-foreground">Net Revenue</span>
              </div>
              <span
                className={`text-base font-bold ${netRevenue >= 0 ? 'text-emerald-600' : 'text-red-600'}`}
              >
                ${fmt(netRevenue)}
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const Row = ({
  icon: Icon,
  color,
  bg,
  label,
  sublabel,
  value,
  negative,
}: {
  icon: React.ElementType;
  color: string;
  bg: string;
  label: string;
  sublabel?: string;
  value: number;
  negative?: boolean;
}) => (
  <div className={`flex items-center justify-between p-2.5 ${bg} rounded-xl`}>
    <div className="flex items-center gap-2">
      <Icon className={`w-3.5 h-3.5 ${color}`} />
      <div>
        <p className="text-xs font-medium text-foreground">{label}</p>
        {sublabel && <p className="text-[9px] text-muted-foreground">{sublabel}</p>}
      </div>
    </div>
    <p className={`text-sm font-bold ${negative ? 'text-red-500' : color}`}>
      {negative ? '-' : ''}${fmt(value)}
    </p>
  </div>
);

const CountRow = ({
  icon: Icon,
  color,
  bg,
  label,
  value,
}: {
  icon: React.ElementType;
  color: string;
  bg: string;
  label: string;
  value: number;
}) => (
  <div className={`flex items-center justify-between p-2.5 ${bg} rounded-xl`}>
    <div className="flex items-center gap-2">
      <Icon className={`w-3.5 h-3.5 ${color}`} />
      <p className="text-xs font-medium text-foreground">{label}</p>
    </div>
    <p className={`text-sm font-bold ${color}`}>{value}</p>
  </div>
);

export default RevenueBreakdown;
